const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

/** What the upload returns, and what a turn then carries so the backend can find the file. */
export interface AttachmentRef {
  attachment_id: string;
  filename: string;
  kind: 'pdf' | 'image';
}

// A current access token. KeycloakService is imported only when an upload happens, not at the top of the
// file: it pulls in keycloak-js, an ES-only package that Jest cannot parse, and nothing else here needs it.
const freshAccessToken = async (): Promise<string | undefined> => {
  const { default: KeycloakService } = await import('services/KeycloakService');
  try {
    await KeycloakService.updateToken();
  } catch {
    // Send what we have: the server answers an expired token with a "sign in again" message.
  }
  return KeycloakService.getToken();
};

/**
 * Uploads one file for a conversation. The endpoint checks who is asking: it needs the user's token and
 * the team the file is for, and it takes the uploader's identity from the token, so no user id is sent.
 * A refusal comes back as an Error carrying the server's own reason (expired session, not on the team,
 * type or size), which is what the user and a bug report need.
 */
export const uploadExportDesignAttachment = async (
  file: File,
  clfId: string,
  groupId: string,
): Promise<AttachmentRef> => {
  const token = await freshAccessToken();
  const formData = new FormData();
  formData.append('clf_id', clfId);
  formData.append('group_id', groupId);
  formData.append('file', file);
  const response = await fetch(`${apiUrl}/export-design/attachments`, {
    method: 'POST',
    // No Content-Type: the browser sets it, with the multipart boundary.
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || `Upload failed for ${file.name}`);
  }
  return response.json();
};
